// Sandbox authoring guide — printed by `cofactor sandbox guide v1 [section]`
// and also rendered (in part) in the web UI on the sandbox detail page.

import type { GuideSection } from './types.js';
import buildCaching from './sandbox-guide/build-caching.txt';

const GOAL: GuideSection = {
  title: 'Goal & Tools',
  slug: 'goal',
  content: `
Your goal is to turn a git repository into a running, containerized development
environment. The end state is a sandbox URL that loads the app in a browser.

WORKFLOW:
  1. canary sandbox create <property>          Create a template + authoring instance
  2. Research the app                          Read the codebase to understand what it needs
  3. Set config values                         API keys, secrets → pushed to instance as .canary/.env
  4. Upload artifacts                          Dockerfile, docker-compose, setup script, etc. → pushed to instance
  5. canary sandbox build <instance-id>        Build images, start containers, generate nginx
  6. Check diagnostics & logs                  Verify the app is running
  7. Fix artifacts, re-upload, re-build        Iterate until the app loads in browser
  8. Mark checklist items complete              Record what you did

Each artifact upload and config set immediately pushes the file to the instance.
The "build" command triggers the actual rebuild and restart.
Batch your changes (upload all artifacts, set all config), then build once.

YOUR TOOLS:

  Config — set environment variables (pushed to instance .canary/.env immediately):
    canary sandbox config set <template-id> <key> <value>
    canary sandbox config list <template-id>

    SANDBOX_URL is auto-injected — use it in compose for browser-facing URLs.
    SANDBOX_LLM_PROXY_URL + SANDBOX_LLM_PROXY_TOKEN are auto-injected too: point an
    OpenAI-compatible client at \${SANDBOX_LLM_PROXY_URL}/azure/v1 with the token as
    its API key and the app shares the platform's model account — no vendor key in config.

  Artifacts — files that define the sandbox (pushed to instance on upload):
    canary sandbox artifact create <template-id> <type> <file>
    canary sandbox artifact get <template-id> <type>

    Types: dockerfile, docker-compose, setup-script, startup-script, seed-script, file

  Build — rebuild and start services:
    canary sandbox build <instance-id>
    canary sandbox build <instance-id> --no-wait

    Generates nginx routing, injects Docker networking, writes file artifacts,
    runs setup script, then runs compose up, then runs startup script.
    Call this after uploading artifacts.

    First builds on large projects may exceed gateway timeouts. The CLI handles
    this automatically — it detects the timeout and polls diagnostics until
    containers are healthy. Use --no-wait to fire-and-forget and poll manually.

  Diagnostics & logs — check container health and read service output:
    canary sandbox diagnostics <instance-id>
    canary sandbox logs <instance-id> --service <name>

  Run-command — escape hatch for debugging and one-off commands:
    canary sandbox run-command <instance-id> "<command>"

  Checklist — record your progress:
    canary sandbox checklist <template-id>
    canary sandbox checklist complete <template-id> <item-id> [options]
    canary sandbox checklist skip <template-id> <item-id>

PRINCIPLES:
  - Artifacts are the source of truth. Every fix must update the artifact file and re-upload.
  - Never patch source files. Use Docker env vars, CMD overrides, setup/startup scripts.
  - Code is volume-mounted at /workspace, not baked into the image.
  - The platform handles nginx routing and Docker networking — you don't need to.
`.trim(),
};

const RESEARCH: GuideSection = {
  title: 'Research the App',
  slug: 'research',
  content: `
Before writing any artifacts, research the application thoroughly.

1. ENV VAR VALIDATION
   Find the app's environment validation schema (Zod, Joi, dotenv, etc.).
   List ALL required (non-optional) variables. Many apps fail fast on missing vars.

   Common locations:
     - src/env.ts, src/config.ts, src/environment.ts
     - .env.example, .env.sample

   Classify each var:
     - AUTO-WIRED: Internal service URLs (DATABASE_URL, REDIS_URL) — hardcode in compose
     - PLACEHOLDER: OAuth creds, S3 buckets — use dummy values, app boots without them
     - SENSITIVE: API keys (ANTHROPIC_API_KEY) — goes in template configSchema
     - DISABLED: Worker flags, schedulers — set to false in compose

2. REQUIRED SERVICES
   What databases, caches, queues does the app need?
   Check: docker-compose.yml, package.json deps, infra/ configs, README
   Common: PostgreSQL, Redis, MongoDB, RabbitMQ, Elasticsearch

3. MIGRATION STRATEGY
   Decide whether migrations can run BEFORE the app server boots.

   Prefer a first-class \`migrate\` compose service when the migration command
   can run in the app image without the app server already running:
     - Depend on the DB healthcheck and any deps/install one-shot.
     - Run the project-local ORM CLI with the same env/volumes as the app.
     - Set restart: "no".
     - Gate schema-reading app services on migrate with
       service_completed_successfully.

   This is the safest pattern for apps that read schema or knobs during boot:
   the API starts once, after the DB is already migrated.

   Use the startup script fallback only when the migration command must exec into
   an already-running app container or depends on runtime state that only exists
   after compose up:
     - Drizzle: sb-compose exec -T app-api bash -c 'cd /app/packages/db && bun run drizzle-kit migrate'
     - Prisma:  sb-compose exec -T app-api bash -c 'bun run prisma migrate deploy'
     - Rails:   sb-compose exec -T app-api rails db:migrate
     - Django:  sb-compose exec -T app-api python manage.py migrate

   Do NOT use bunx for JS migrations — it downloads a fresh copy. Use "bun run" instead.

4. PACKAGE BUILD ORDER (monorepos)
   Which workspace packages export from dist/ and must be pre-built?
   Check package.json "main" or "exports" fields for "./dist/" references.
   Prefer a one-shot build service (for example web-build) and gate services
   that import those dist packages on service_completed_successfully. Use
   startup.sh only as a fallback when the build must happen after compose up.
   Do NOT bake these outputs into the Dockerfile; source is volume-mounted.
   When running scripts via sb-compose exec, cd to the package directory where
   the dependency is declared. Bun workspace module resolution won't find deps
   from the monorepo root.

5. PRODUCTION BUILD STRATEGY (frontends)
   Sandboxes serve a PRODUCTION build of the frontend, not a dev server.
   This is faster and far more responsive, ships compressed/hashed/cacheable
   assets, and sidesteps dev-server host-allowlist headaches entirely (a static
   file server has no notion of "allowed hosts"). HMR / hot-reload is
   intentionally dropped — a sandbox demos and QAs a BUILT app; it is not a
   live-edit environment.

   Identify, from package.json scripts and the framework:
     - The production build command (vite build, next build, ng build,
       webpack --mode production, etc.) and its output directory
       (commonly dist/, build/, or .next/).
     - Whether the framework produces a STATIC bundle (Vite, CRA, Angular,
       plain Webpack → a dist/ of static files) or needs a NODE SERVER at
       runtime (Next.js server mode, Remix, Nuxt SSR). Static bundles are
       served by nginx; server-rendered apps run their production start
       command (next start, etc.) as a normal routed service.

   BUILD-TIME ENV VARS:
     Bundlers BAKE build-time public vars into the output, so they must be set
     in the environment BEFORE the build runs — setting them only in compose
     at runtime is too late for a static bundle. Find every build-time-public
     var (VITE_*, NEXT_PUBLIC_*, REACT_APP_*, ng-injected config, etc.),
     especially the browser-facing API base URL, and set them just before the
     build using \${SANDBOX_URL} for any URL the browser must reach
     (e.g. VITE_API_URL=\${SANDBOX_URL}/api). See Artifacts → PRODUCTION BUILD
     for where the build runs.

   COST: production builds are slower and more memory-hungry than starting a
   dev server. Vite/webpack builds can OOM the default Node heap on large
   apps — be ready to raise it (NODE_OPTIONS=--max-old-space-size=8192) and
   expect the build to add minutes to the build step.

6. WORKERS & DAEMONS TO DISABLE
   Sandbox typically only needs the API server + web frontend.
   Disable: job runners, schedulers, stall detectors, cron, coordinators.
   Look for env flags like WORKER_ENABLED, SCHEDULER_ENABLED, etc.
`.trim(),
};

const ARTIFACTS: GuideSection = {
  title: 'Artifacts',
  slug: 'artifacts',
  content: `
Artifacts are the files that define your sandbox. Write them locally, then upload:
  canary sandbox artifact create <template-id> <type> <local-file>

IMPORTANT — ARTIFACT SCOPE:
  Artifact uploads are TEMPLATE-level, not instance-level. Uploading an artifact
  updates the template definition and pushes the file to the AUTHORING instance
  only. Running instances provisioned from the template are NOT updated — they
  use whatever was baked in at provision time. To apply artifact changes to an
  existing non-authoring instance, you must rebuild it:
    canary sandbox build <instance-id>

PLATFORM ARCHITECTURE:
  The sandbox filesystem is split into two areas:
    - /workspace           checked-out repo and user-owned files
    - /workspace/.canary   platform-managed compose, config, and helper scripts

  Your compose file is saved as /workspace/.canary/docker-compose.override.yml and
  merged with the platform's base compose at /workspace/.canary/docker-compose.yml,
  which provides:
    - nginx-proxy: reverse proxy on port 80 (routes based on sandbox.route labels)
    - sandbox-agent: platform agent on port 8080
    - minio: S3-compatible object store at http://minio:9000 (creds:
      minioadmin / minioadmin), with a sandbox-data bucket. See S3 BUCKET
      below — the platform doesn't wire the endpoint into your services.

  You do NOT need to declare any of these in your docker-compose.yml — the
  platform injects them. Defining a service with one of these reserved
  names is rejected at artifact-upload time with code
  SANDBOX_RESERVED_SERVICE_NAME. The full reserved list is:
    nginx-proxy
    sandbox-agent
    minio
    docker-socket-proxy

  CONTAINER PATHS — the bind mount maps the repo root to /app inside
  containers, while file artifacts are uploaded under /workspace/.canary
  on the host. So the same file has two paths:
    /workspace/.canary/foo.ts   ← the path you upload to from the CLI
    /app/.canary/foo.ts         ← the path your compose command/services
                                  reference inside the container
  Reference the in-container path in any compose \`command\` array, exec
  call, or script file path that runs inside a service. The host-side
  /workspace path only appears in startup.sh / setup.sh (which run on
  the host, not inside a container).

  S3 BUCKET (S3 emulation):
  The platform runs an S3-compatible server as the "minio" service at
  http://minio:9000, and the deploy waits until the sandbox-data bucket
  exists before it starts your services. After a restart, when both come up
  together, retry the first S3 call on connection errors. It does NOT inject
  AWS_ENDPOINT_URL_S3 into your containers, so:

    1. Wire the endpoint into your service env in compose:
         AWS_ENDPOINT_URL_S3: http://minio:9000
         AWS_ACCESS_KEY_ID: minioadmin
         AWS_SECRET_ACCESS_KEY: minioadmin
         AWS_REGION: us-east-1
         S3_BUCKET_NAME: sandbox-data

    2. If your app needs a bucket with another name, create it through the
       S3 API (CreateBucket) from your app's own startup or seed code. No
       extra compose service is needed for bucket setup.

  Most AWS SDK v3 clients honor AWS_ENDPOINT_URL_S3 natively. If your
  app passes a custom S3 client, also set forcePathStyle: true (the
  sandbox S3 server doesn't support virtual-host-style bucket addressing).

  Compose still runs with /workspace as the project directory. That means user
  compose content keeps the same repo-root-relative behavior for build contexts,
  bind mounts, and referenced files.

  COMPOSE INVOCATION: use the pre-installed "sb-compose" wrapper on the host.
  It expands to docker compose with the canonical flag set:
      docker compose --project-directory /workspace \\
        --env-file /workspace/.canary/.env \\
        -f /workspace/.canary/docker-compose.yml \\
        -f /workspace/.canary/docker-compose.override.yml \\
        "$@"
  Call it from startup scripts, action scripts, and run-command the same way:
      sb-compose exec -T app-api bash -c 'bun run build'
      sb-compose ps
      sb-compose logs --tail 50 app-api
  The wrapper is available as /usr/local/bin/sb-compose (symlink) and
  /workspace/.canary/sb-compose. If you invoke raw "docker compose" yourself
  you must pass the same flags — otherwise paths and variable interpolation
  (\${SANDBOX_URL} et al.) will resolve incorrectly.

  The platform injects a shared Docker network. On "build", the sequence is:
    1. Write file artifacts to their destination paths
    2. Run setup script (if present)
    3. docker compose down (cleanup previous containers)
    4. docker compose build + up
    5. Reload nginx
    6. Wait for long-running services to accept "sb-compose exec" before continuing
    7. Run startup script (if present)

  Step 6 is automatic. You don't need to add your own "wait for postgres"
  loops at the top of startup.sh — by the time startup.sh runs, every
  non-one-shot service is exec-able. Service healthchecks inside the
  containers (e.g. postgres pg_isready) are still your responsibility if
  your app needs to wait on them.

  Do NOT define services named "nginx-proxy", "sandbox-agent", or "minio"
  in your compose.

DOCKERFILE (type: dockerfile)
  A base image that provides the runtime. Source code is volume-mounted, NOT copied in.
  Written to /workspace/.canary/Dockerfile.sandbox (avoids conflicting with the repo's own Dockerfile).
  Reference it in compose as: build: { context: ., dockerfile: .canary/Dockerfile.sandbox }

  Structure:
    FROM <runtime-base>
    RUN apt-get update && apt-get upgrade -y && apt-get install -y <system-deps>
    RUN npm install -g <global-tools>
    WORKDIR /app

  Rules:
    - Always "apt-get upgrade -y" before installing — base images ship OS
      packages with known CVEs; the upgrade patches them at build time
    - NO "COPY . ." — code comes via volume mount at /workspace
    - NO "RUN npm build" — the production build runs on the instance as a
      compose service (see PRODUCTION BUILD), where \${SANDBOX_URL} and
      build-time env are available. Baking it into the image would freeze
      build-time env to image-build values.
    - Include ALL system deps (chromium, ffmpeg, python, etc.)
    - For browser automation: use mcr.microsoft.com/playwright as base
    - Do NOT use ENTRYPOINT — it breaks one-shot services (deps, migrate) whose
      command gets passed as args to the entrypoint instead of running directly

DOCKER COMPOSE (type: docker-compose)
  Defines all services. Platform handles networking and nginx routing.

  Typical services:
    deps (one-shot)    — installs dependencies into named volume, restart: "no"
    database           — official image with healthcheck (postgres, mysql, etc.)
    cache              — redis/memcached with healthcheck
    web-build (one-shot) — runs the production frontend build into the bind-mounted
                         repo tree, restart: "no" (see PRODUCTION BUILD)
    app-api            — application server, PRODUCTION command (NODE_ENV=production,
                         no --hot / --watch)
    app-web            — static frontend served by nginx:alpine from the build
                         output (see PRODUCTION BUILD). SSR frameworks (Next.js
                         server mode, etc.) run their production start command
                         instead of an nginx static server.

  MIGRATIONS: prefer a first-class \`migrate\` one-shot compose service when
  migrations can run before the app boots. Gate it on the DB healthcheck and
  dependency install/build services it needs, then gate app-api on migrate with
  service_completed_successfully. This prevents pre-migration boot crashes and
  avoids a post-hoc "restart app-api" recovery step.

    migrate:
      build: { context: ., dockerfile: .canary/Dockerfile.sandbox }
      working_dir: /app
      volumes:
        - .:/app
        - node_modules:/app/node_modules
      command: ["bash","-c","cd /app/packages/db && bun run drizzle-kit migrate"]
      depends_on:
        deps:
          condition: service_completed_successfully
        postgres:
          condition: service_healthy
      restart: "no"

    app-api:
      depends_on:
        migrate:
          condition: service_completed_successfully

  If a migrate service fails because the command/path/env is wrong, fix the
  artifact and run a fresh sandbox build so compose recreates the one-shot.
  Use startup-script migrations only for stacks that truly require an already
  running app container.

  ROUTING LABELS — declare how nginx should route to your services:
    services:
      app-api:
        labels:
          sandbox.route: "/api"
          sandbox.port: "3000"
      app-web:
        labels:
          sandbox.route: "/"
          sandbox.port: "80"

    The platform reads these labels and generates nginx config on "build".
    If no labels, all traffic routes to the first non-infrastructure service.

    PREFIX STRIPPING: the generated nginx location uses a trailing-slash
    proxy_pass (proxy_pass http://<service>:<port>/), so the matched prefix
    is stripped from the upstream request. With sandbox.route: "/api", a
    browser hit to "/api/health" is delivered to the upstream as "/health".
    Pick VITE_API_URL, OAuth redirect URIs, etc. accordingly.

  !!! GET /health MUST RETURN 200 !!!
    Whatever service carries sandbox.route: "/" has to answer GET /health with
    a 200. The provision health gate curls localhost:80/health and FAILS THE
    WHOLE PROVISION (after ~3 minutes of retries) if it doesn't — the instance
    is marked failed even though every container came up clean.

    An app server usually has this already. A STATIC service does not: an
    nginx:alpine serving a built SPA 404s /health unless you give it one.

    Two ways, and note that BOTH need the file to reach the served document
    root — uploading an artifact somewhere under .canary/ does nothing on its
    own, because nginx serves its root, not the host filesystem:

    1. An explicit location block in your nginx conf (no extra mount needed if
       you already mount a conf):

         location = /health { return 200 'ok'; add_header Content-Type text/plain; }

    2. A file artifact that you then MOUNT into the document root:

         cofactor sandbox artifact create <template-id> file health \\
           --path /workspace/.canary/health

       and in compose:

         volumes:
           - ./.canary/health:/usr/share/nginx/html/health:ro

       Keep the artifact destination a single filename directly under
       .canary/ — nested destinations conflict with the provision payload
       validator.

    This bites hardest on sandboxes that serve nothing interesting on "/" — a
    test-runner box, a worker-only template — where the routed service exists
    purely to satisfy the gate. It still needs /health.

  ENV VAR STRATEGY — use \${VAR} syntax, values come from config + .env:
    - Auto-wired (hardcode):   DATABASE_URL=postgres://user:pass@postgres:5432/db
    - From config:             ANTHROPIC_API_KEY=\${ANTHROPIC_API_KEY}
    - Platform-injected:       VITE_API_URL=\${SANDBOX_URL}/api
    - Platform model account:  OPENAI_BASE_URL=\${SANDBOX_LLM_PROXY_URL}/azure/v1
                               OPENAI_API_KEY=\${SANDBOX_LLM_PROXY_TOKEN}
    - Disabled:                WORKER_ENABLED=false

    SANDBOX_URL is auto-injected by the platform (e.g. https://<id>.env.runcofactor.com).
    Use it for any URL a browser needs to reach the sandbox.
    SANDBOX_LLM_PROXY_URL / SANDBOX_LLM_PROXY_TOKEN are the per-instance LLM proxy: an
    OpenAI-compatible client pointed at \${SANDBOX_LLM_PROXY_URL}/azure/v1 with the token
    as its key is rewritten onto the platform's funded Azure account. Prefer this over
    asking the user for a vendor API key.

  Rules:
    - Use named volumes for data persistence (postgres-data, redis-data, etc.)
    - Bind-mount the repo (.:/app) rather than using named volumes for
      node_modules. Per-package node_modules named volumes silently shadow the
      bind mount on any service that doesn't mount them — a common monorepo
      trap where "bun run build" fails because tsup is nowhere to be found.
      The sandbox host is Linux EC2; bind mounts are fast.
    - DO NOT define networks — the platform handles networking
    - DO NOT expose ports to the host — nginx handles routing
    - Keep relative paths repo-root-relative; compose runs from /workspace even
      though the platform-managed compose files live in /workspace/.canary
    - Escape shell variables in "command:" strings with $$ so compose leaves
      them for the shell (e.g. command: ["bash", "-c", "exec foo $$BAR"]).
      Unescaped \${VAR} is substituted at compose-time from the env file.

PRODUCTION BUILD (static frontends)
  A production build served by nginx is the RECOMMENDED default — faster,
  cacheable, and free of dev-server host-allowlist headaches. It is NOT mandatory,
  though: a dev server (Vite/webpack) with a permissive host allowlist (e.g. Vite
  \`allowedHosts: true\`) also works and is simpler to stand up. Reach for the
  production build when the dev bundle is large enough to render blank on a remote
  sandbox (hundreds of uncompressed modules) or when you want a demo-grade,
  responsive UI. The production build has two pieces:

  1. BUILD as a one-shot compose service — in its OWN container, NOT inside the
     api/app container. A production build is CPU + disk-I/O heavy; if it runs
     inside the api container it starves the api's /health endpoint and the
     provision health gate fails the whole sandbox (nginx 502). The setup script
     can't do it either (it runs on the HOST, where app runtimes don't exist, and
     before deps installs). So: a dedicated one-shot service that depends on deps.

     Build the RIGHT set of packages. In a monorepo, build EVERY workspace
     package that exports from dist/ that is imported by BOTH the frontend AND
     the BACKEND — not just the UI. If the api imports a dist-exporting package
     whose dist is missing, the api crash-loops "Cannot find module @scope/pkg"
     and never answers /health. Find them: grep the api's imports for @scope/*
     and cross-check each package.json "main"/"exports" for "./dist/". Build the
     leaf packages first, then the app:
       web-build:
         build: { context: ., dockerfile: .canary/Dockerfile.sandbox }
         working_dir: /app
         volumes:
           - .:/app
         environment:
           VITE_API_URL: \${SANDBOX_URL}/api      # build-time public vars FIRST
           NODE_OPTIONS: --max-old-space-size=8192  # raise heap if the build OOMs
         # nice -n 19 keeps the api responsive if anything overlaps. Build the
         # api's dist-dependencies AND the ui, then the app bundle.
         command: ["bash","-c","nice -n 19 bash -c 'cd /app/packages/foo && bun run build && cd /app/packages/bar && bun run build && cd /app/apps/web && bun run build'"]
         depends_on:
           deps:
             condition: service_completed_successfully
         restart: "no"
     Compose interpolates \${SANDBOX_URL} at "up" time, so the bundle bakes the
     correct browser-facing API URL. The output (dist/) lands in the
     bind-mounted repo tree, where the nginx service below mounts it.

     CACHEABILITY: if you want this build cached across sandboxes (see Build
     Caching), the output must NOT bake \${SANDBOX_URL} — a cached dist is reused on
     other sandboxes. Resolve the API origin at runtime from the browser location
     (web and api are same-origin behind the proxy, so a relative "/api" works
     everywhere) instead of baking VITE_API_URL. Then label web-build with
     sandbox.cache + sandbox.cache-inputs/paths.

     SERIALIZE the build BEFORE the health gate: give nginx and any app service
     that imports dist-exporting packages a depends_on web-build
     (service_completed_successfully). That makes "docker compose up" block
     until the build finishes, so the api boots once with all dist packages
     present. Do not rely on startup.sh to restart app-api solely to recover
     from missing build outputs.

  2. SERVE the output with a lightweight nginx:alpine service that depends on
     the build completing. Mount the PARENT of the output dir (e.g. apps/web),
     NOT the dist dir directly, and point nginx root at the dist SUBPATH:
       app-web:
         image: nginx:alpine
         volumes:
           - ./apps/web:/srv:ro          # mount the PARENT, root is /srv/dist
           - ./.canary/nginx-spa.conf:/etc/nginx/conf.d/default.conf:ro
         depends_on:
           web-build:
             condition: service_completed_successfully
         labels:
           sandbox.route: "/"
           sandbox.port: "80"

     WHY mount the parent: bundlers (vite, webpack) DELETE and recreate the
     output dir on build, giving it a NEW inode. Docker creates an empty
     dist bind-source at compose-up time; a direct "dist:/usr/share/nginx/html"
     bind pins nginx to that stale empty inode and it serves 403 "directory
     index forbidden" forever. Mounting the parent (a stable inode) and letting
     nginx resolve root/<uri> per request always sees the current dist.

     Provide an SPA-fallback nginx config as a file artifact so client-side
     routes (deep links / hard refresh) don't 404 — unknown paths serve
     index.html and the client router takes over:
       canary sandbox artifact create <template-id> file nginx-spa.conf \\
         --path /workspace/.canary/nginx-spa.conf

     nginx-spa.conf:
       server {
         listen 80;
         root /srv/dist;            # the dist SUBPATH of the mounted parent
         index index.html;
         gzip on;
         gzip_types text/css application/javascript application/json image/svg+xml;
         location /assets/ {
           try_files $uri =404;
           expires 1y;
           add_header Cache-Control "public, immutable";
         }
         location / {
           try_files $uri $uri/ /index.html;
           add_header Cache-Control "no-cache";
         }
       }

     (Match root to your framework's output dir under the mounted parent — e.g.
     /srv/build for a build/ output. The hashed-asset location block assumes a
     /assets/ dir — adjust to your bundler's asset path.)

  WHY: nginx serving static files has no host allowlist (no "This host is not
  allowed"), ships compressed/hashed/cacheable assets, and is far more
  responsive than a dev server streaming hundreds of uncompressed modules. The
  trade-off is no hot-reload and a longer, heavier build step — acceptable
  because sandboxes demo a BUILT app.

  SSR / server-rendered frameworks (Next.js server mode, Remix, Nuxt SSR):
  skip the nginx static server. Build in the one-shot service, then run the
  framework's production start command (e.g. next start) as a normal routed
  service with sandbox.route "/" and its listen port.

SETUP SCRIPT (type: setup-script)
  Runs BEFORE docker compose up on every build. Runs on the HOST, not in a container.
  Written to /workspace/.canary/setup.sh.

  IMPORTANT: The host is a bare EC2 instance with only docker and basic linux tools.
  App runtimes (node, bun, python, ruby) are NOT available on the host — they live
  inside your Docker images. Do NOT run app commands directly here.

  Script authoring rules:
    - Treat /workspace as the repo root for any file access or repo command
    - Do not assume the current directory matches the script's own path
    - Use explicit paths or start with "cd /workspace" before repo-relative work

  Use for host-level preparation only:
    - Installing system packages (apt-get, yum)
    - Downloading external tools or binaries
    - Generating config files programmatically (prefer file artifacts for static files)

  For most apps, you won't need a setup script — file artifacts and the startup
  script cover the common cases. Skip this if you don't need host-level prep.

  Rules:
    - Must be idempotent — safe to run multiple times
    - Never patch source files
    - Non-zero exit is logged as a warning but does not block the build

STARTUP SCRIPT (type: startup-script)
  Runs AFTER docker compose up on every build. Runs on the HOST, not in a container.
  Written to /workspace/.canary/startup.sh.

  This is where most app setup happens. Containers are running, so you can use
  "sb-compose exec" to run commands inside them with the app runtime.

  Script authoring rules:
    - Start with "cd /workspace" or use explicit /workspace/... paths
    - Do not assume the script runs from /workspace/.canary
    - Prefer sb-compose exec for anything that needs the app runtime
    - Use -T with exec (no TTY allocation — startup runs headless)

  Use for:
    - DB migrations via ORM CLI only when a pre-boot migrate service is not viable
      (use "bun run", NOT bunx — bunx downloads fresh)
    - Building workspace packages that export from dist/ only when a pre-boot
      build service is not viable
    - Waiting for services to be healthy
    - Triggering initial data loads

  WHEN STARTUP-SCRIPT MIGRATIONS ARE USED:
    ORMs that introspect the schema at boot (Drizzle/effect-sql, Prisma with
    prepared statements, SQLAlchemy with autoload, etc.) can cache the
    pre-migration schema on the first query. If migrations run after the API has
    already booted, restart schema-reading services after the migration or
    you'll see errors like:
      relation "<table>" does not exist
    even though psql confirms the table is there.

    When migrations run as a first-class compose service and app-api depends on
    it, this restart is unnecessary: app-api has not booted yet.

  Example (Bun monorepo with pre-boot build + migrate services):
    #!/bin/bash
    set -e
    # web-build and migrate already completed before app-api started via
    # depends_on: service_completed_successfully.
    # Restart only services whose runtime config or mounted files changed here.
    sb-compose restart app-web
    # Wait for API to be healthy
    for i in $(seq 1 30); do
      sb-compose exec -T app-api curl -sf http://localhost:3000/health && break
      sleep 2
    done

  Rules:
    - Must be idempotent — safe to run multiple times
    - Never patch source files
    - Don't pre-wait for services to START — the platform handles that before
      startup.sh runs (see Compose Architecture section). Do wait for service
      *readiness* (app health, schema migrations applied) when your app
      depends on it.

FILE (type: file)
  Arbitrary sandbox-owned files written to the instance at a specific path.
  Use for config files that need to exist before services start (e.g. Vite
  wrapper configs, one-shot scripts that need app dependencies).

  !!! File artifacts MUST be written under /workspace/.canary/ !!!
    The repo tree (everything outside .canary/) is user-owned. Sandbox
    helpers live in .canary/ so they never collide with real files and
    are never committed back to the repo. The API will reject any
    --path value outside /workspace/.canary/ with
    SANDBOX_FILE_ARTIFACT_OUTSIDE_CANARY.

    If your script needs app dependencies (e.g. \`postgres\` from the api
    package), put a tiny package.json next to it in .canary/scripts/ and
    \`bun install\` in the setup-script, OR invoke it via
    \`sb-compose exec -T <svc> ...\` so it runs inside a container that
    already has them.

  Upload with --path to specify the destination:
    canary sandbox artifact create <template-id> file <local-file> --path /workspace/.canary/<dest>

  Example — SPA fallback config for the static web server (see PRODUCTION BUILD):
    canary sandbox artifact create <template-id> file nginx-spa.conf \\
      --path /workspace/.canary/nginx-spa.conf

  ONE ROW PER FILENAME. File artifacts are keyed by (template, filename).
  Uploading a file with the same filename as an earlier upload REPLACES
  the earlier row. The CLI prints "(replaced)" on such uploads, and the
  next build removes the previous destination path so the instance
  filesystem mirrors the template set exactly.

  If you want to retire a file, just upload something else with a
  different filename — the stale file is cleaned up on next build via
  .canary/.artifact-paths (a manifest the platform maintains).

  File artifacts are written before the setup script runs, so they're
  available for both setup and compose services.

SEED SCRIPT (type: seed-script)
  Runs once on first provision to populate demo data. Optional.
  Written to /workspace/.canary/seed.sh. Runs on the HOST after startup.sh.
  Skipped on subsequent builds (checks /workspace/.canary/.seeded).

  For more than a trivial insert — a real demo dataset, a multi-script seed
  pipeline, or wiring login personas — see the SEED & PERSONAS section
  ("cofactor sandbox guide v1 seed-personas"). Two things bite there: a long seed must
  run DETACHED (it far outlasts the provision/verify window), and app-defined
  "demo personas" usually land in a DEFAULT workspace, not the one you seeded.

CONFIG SCHEMA
  Define which env vars need user input. Set when completing the docker-compose checklist item:
    canary sandbox checklist complete <template-id> docker-compose-created \\
      --status success \\
      --summary "Compose with postgres, redis, api, web" \\
      --config-schema '[
        {"key": "ANTHROPIC_API_KEY", "required": true, "sensitive": true, "description": "Anthropic API key"},
        {"key": "OPENAI_API_KEY", "required": false, "sensitive": true, "description": "OpenAI API key"}
      ]'

  Set defaults:
    canary sandbox config set <template-id> <key> <value>
    canary sandbox config list <template-id>

  ⚠️ The provision/verify pre-check FAILS ("Missing environment variables: X") for
  any config-schema key — even required:false — that has no value AND is referenced
  as \${X} in compose. So do not wire an unset optional var into compose: give it a
  config default, or drop the \${X} reference until a value is provided.
`.trim(),
};

const VERIFICATION: GuideSection = {
  title: 'Verification & Iteration',
  slug: 'verify',
  content: `
After running "canary sandbox build", verify the sandbox is working. The iteration loop:
  upload artifacts → build → check diagnostics → read logs → fix → re-upload → rebuild

COMMANDS:
  # Check all container statuses at a glance
  canary sandbox diagnostics <instance-id>

  # Read logs for a specific service
  canary sandbox logs <instance-id> --service app-api
  canary sandbox logs <instance-id> --service app-web
  canary sandbox logs <instance-id> --service postgres

  # Run arbitrary commands on the instance (runs on the host VM by default)
  canary sandbox run-command <instance-id> "docker compose ps"
  canary sandbox run-command <instance-id> "echo hello"

  # Run commands inside a specific Docker container
  canary sandbox run-command <instance-id> --service app-api "bun --version"
  canary sandbox run-command <instance-id> --service app-api "bun test"

  # Test health endpoints. localhost:80/health is the one the PROVISION GATE
  # curls — a non-200 here fails the whole provision, so check it before you
  # verify (see "GET /health MUST RETURN 200" in the artifacts section).
  canary sandbox run-command <instance-id> "curl -s -o /dev/null -w '%{http_code}' http://localhost:80/health"
  canary sandbox run-command <instance-id> "curl -s http://localhost:3000/health"
  canary sandbox run-command <instance-id> "curl -s -o /dev/null -w '%{http_code}' http://localhost:80/"

  # Test with sandbox hostname (for host-restriction issues)
  canary sandbox run-command <instance-id> \\
    "curl -s -H 'Host: <instance-id>.env.runcofactor.com' http://localhost:80/"

SANDBOX URL:
  https://<instance-id>.env.runcofactor.com

IN-BOX BROWSER VERIFICATION (sandbox agent):
  The agent running inside the sandbox has a headless Chromium and can drive the
  running app directly to confirm setup — no external tester needed. The app is
  in a sibling docker-compose project; target it over the compose network.

    docker compose ps                                  # find app service + port
    cofactor session start --headless --url http://<svc>:<port> [--credential <n>]
    cofactor session snapshot                          # page accessibility tree
    cofactor session click --ref <ref>                 # act on a ref
    cofactor session type --ref <ref> --text "..." --submit
    cofactor session wait --text "<expected copy>"     # assert it loaded
    cofactor session console --only-errors             # surface JS errors
    cofactor session stop

  The session daemon idle-exits after ~60s; just start again if a command
  reports no active session. Run this AFTER the app is healthy (get_status /
  diagnostics), then record the result via update_checklist.

IF SOMETHING FAILS:
  1. Read the logs for the failing service
  2. Diagnose the root cause (missing env var, failed build, wrong port, etc.)
  3. Fix the ARTIFACT — not just the running container
  4. Re-upload: canary sandbox artifact create <template-id> <type> <file>
  5. Rebuild: canary sandbox build <instance-id>
  6. Verify again

WHEN A CLI COMMAND TIMES OUT AT 30s:
  Long sandbox operations (build, edit, artifact create on first push,
  occasionally diagnostics) can exceed the gateway window even though
  the work is still running server-side. The CLI prints:
    Error: Request to https://api.trycanary.ai/... timed out after 30000ms.

  Don't retry blindly — a retry can spawn duplicate work (e.g. a second
  authoring instance). Instead, poll for the actual state:

    canary sandbox template <template-id>     # status: provisioning/authoring/verified
    canary sandbox diagnostics <instance-id>  # container/app health
    canary sandbox list                       # running instances

  Only retry once you've confirmed via polling that the prior call
  truly didn't take effect.

WHEN VERIFY HANGS:
  \`canary sandbox verify\` now streams container state and failing-service
  log tails every ~30s while the probe's app is unreachable. If you see:

    >> nginx-proxy is restart-looping because upstream '<service>' (...) is exited.
       Read that service's logs above, not nginx's.

  ignore nginx entirely and read the upstream's tail — nginx is the symptom,
  not the cause. Common underlying causes:
    - the upstream service crashed at startup (bad env var, missing file,
      import error)
    - a file artifact was lost because you re-uploaded over its filename
      (shouldn't happen any more — the build-time reconcile handles this —
      but watch for it if you're on an old template)

  On failure, the probe instance is preserved by default so you can
  \`canary sandbox run-command <id>\` into it. Pass
  --auto-terminate-on-failure for CI.

COMMON ISSUES:

  "Invalid environment" / env validation fails
    App has strict env var validation. Read the env schema. Add ALL required vars to compose.

  Migration fails with "SST links not active" / cloud SDK error
    Migrate script depends on cloud tooling. Use the project-local ORM CLI
    directly. Prefer a first-class migrate service gated on Postgres/deps and
    gate app-api on it; if the stack truly requires an already-running app
    container, use startup.sh with sb-compose exec instead:
      sb-compose exec -T app-api bash -c 'bun run drizzle-kit migrate'
    DATABASE_URL should be hardcoded in the compose environment section.

  Migration/DB script fails with a garbled URL or "server does not support SSL"
    Some migration runners resolve the DB HOST to an IP and string-replace it
    across the whole URL, then append sslmode=require. A host literally named
    "postgres" then corrupts the "postgresql://" scheme (postgres→IP inside
    postgresql) AND forces SSL that local Postgres rejects. Fix: resolve the
    container IP yourself and pass an IP-based DATABASE_URL so the rewrite is a
    no-op (an IP host == its own resolution, so the runner skips the rewrite):
      PG_IP=$(sb-compose exec -T app-api getent ahostsv4 postgres | head -1 | awk '{print $1}')
      sb-compose exec -T app-api bash -c "DATABASE_URL=postgresql://u:p@$PG_IP:5432/db ./scripts/migrate.sh"

  "Cannot find module" / package not built
    Monorepo package exports from dist/ but hasn't been built.
    Add the package to the pre-boot build service and gate any importing app
    service on that build with service_completed_successfully. Use startup.sh
    only if the build truly cannot run before compose up.

  "Cannot find package 'X'" from a file-artifact script
    Bun resolves node_modules starting from the script's directory. File
    artifacts live under /workspace/.canary/ which has no node_modules by
    default. Two fixes:
      (a) add a /workspace/.canary/scripts/package.json declaring the
          dep (e.g. "postgres") and run \`bun install\` from the
          setup-script / inside a sb-compose exec.
      (b) don't run the script on the host at all — invoke it via
          \`sb-compose exec -T <app-service> bun /app/.canary/scripts/foo.ts\`
          so it resolves against the workspace's own node_modules. Replace
          <app-service> with your template's actual service name.

  Blank page — frontend never renders (served by a dev server)
    The web service is a dev server (Vite/webpack-dev-server) shipping hundreds
    of uncompressed, no-cache modules. On a remote sandbox these trickle in too
    slowly for the SPA to mount, so the page stays blank. Confirm with:
      curl -s <SANDBOX_URL>/ | grep '<script'
    Dev mode references /@vite/client or /src/main.tsx; a production build
    references hashed /assets/*.js. Fix: serve a production build (see
    Artifacts → PRODUCTION BUILD), not a dev server.

  Deep links / page refresh 404 (SPA routing)
    nginx serves static files but a client-side route (e.g. /settings) or a
    hard refresh returns 404 — the static server has no SPA fallback. Add
    "try_files $uri $uri/ /index.html;" to the nginx config (see Artifacts →
    PRODUCTION BUILD) so unknown paths serve index.html and the client router
    takes over.

  Frontend loads but API calls hit the wrong URL / localhost
    The browser-facing API URL was not set as a BUILD-TIME var before the
    build, so the bundle baked a default (often localhost:3000). Static
    bundles read build-time vars at BUILD time, not runtime — setting them
    only in the app-web/runtime compose env is too late. Set the public var on
    the web-build service env (or before the build command), using
    \${SANDBOX_URL}, then rebuild:
      VITE_API_URL: \${SANDBOX_URL}/api      # or NEXT_PUBLIC_*, REACT_APP_*, etc.

  API 502 / "Cannot find module '@scope/pkg'" crash-loop after going prod
    The api imports a workspace package that exports from dist/, but that
    package's dist was never built — so the api crash-loops and the health gate
    fails the whole provision with a 502. Easy to miss because the FRONTEND
    build (ui) works. Build EVERY dist-exporting package the api imports in the
    web-build service (see Artifacts → PRODUCTION BUILD), not just the ui ones.
    A warm AMI can hide this (it baked the dist in); a fresh provision exposes it.

  403 "directory index forbidden" from the static server
    nginx is bound to the stale empty dist inode the bundler replaced. Mount the
    PARENT dir and set root to the dist subpath (see Artifacts → PRODUCTION
    BUILD), or restart the nginx service after the build.

  Build is killed / "JavaScript heap out of memory"
    The production build exhausted the Node heap (common on large Vite/webpack
    builds). Raise it on the web-build service before the build:
      NODE_OPTIONS: --max-old-space-size=8192
    If it still OOMs, bump the instance size (canary sandbox set-size) and/or
    the heap further.

  Stale page after a rebuild (old dist served)
    nginx is serving a previous build because the build step didn't re-run or
    its output dir wasn't refreshed. Confirm the web-build service ran in this
    build cycle (canary sandbox logs <id> --service web-build), that it wrote
    to the dir nginx mounts, and — if you ever build via the startup script
    instead — that nginx was restarted (sb-compose restart app-web). Hashed
    asset filenames change per build; if index.html still references old
    hashes, the build didn't land.

  run-command times out
    Long-running operation exceeds the 55s SSM command timeout. Write a script file and run in background:
    canary sandbox run-command <id> "cat > /tmp/run.sh << 'SCRIPT'
    #!/bin/bash
    <commands>
    SCRIPT
    chmod +x /tmp/run.sh && nohup /tmp/run.sh > /tmp/run.log 2>&1 & echo started"
    Then check: canary sandbox run-command <id> "tail -20 /tmp/run.log"

  "Resolving dependencies" hangs in migration container
    bunx/npx downloads a fresh copy instead of using project deps.
    Use "bun run drizzle-kit migrate" instead of "bunx drizzle-kit migrate".

  Containers can't resolve each other by name
    Don't use docker run — use docker compose for everything.

RUNNING TESTS:
  Two approaches depending on your needs:

  Option 1 — Install runtime on host (no Docker needed):
    Use the setup script to install the runtime on the host VM, then run
    tests directly. Good for unit tests that don't need services like a database.

    Example setup-script artifact:
      #!/bin/bash
      curl -fsSL https://bun.sh/install | bash
      export PATH="$HOME/.bun/bin:$PATH"

    Then: canary sandbox run-command <instance-id> "bun test"

  Option 2 — Run inside a container (--service flag):
    Route commands into a running Docker container. Good for integration
    tests that need access to the database or other services.

    canary sandbox run-command <instance-id> --service app-api "bun test"
    canary sandbox run-command <instance-id> --service app-api "bun test:integration"

  \${SANDBOX_URL} is blank / env vars from config not in containers
    The "build" command writes /workspace/.canary/.env (with SANDBOX_URL and all config values)
    before running docker compose up. If the .env is missing or stale, re-run:
      canary sandbox build <instance-id>
    You can also trigger a .env refresh by setting any config value:
      canary sandbox config set <template-id> <key> <value>

  .env values look empty when I cat the file
    Values are written single-quoted (KEY='value'). Short values can look like
    KEY='' at a glance even when they have content — it's just the quotes. Run
    "wc -c" on a line, or "docker compose exec app-api env | grep KEY", to
    confirm the actual value landed.
`.trim(),
};

const CHECKLIST: GuideSection = {
  title: 'Checklist',
  slug: 'checklist',
  content: `
The checklist tracks your progress toward a working sandbox. View it with:
  canary sandbox checklist <template-id>

Each item has a status: pending, completed, skipped, or failed.
Items marked with * are actionable (their dependencies are met).

COMPLETING ITEMS:
  canary sandbox checklist complete <template-id> <item-id> \\
    --status <success|failure> \\
    --summary "What you did"

  Example:
    canary sandbox checklist complete <template-id> environment-analyzed \\
      --status success \\
      --summary "Bun monorepo: Postgres, Redis. Vite production build served by nginx, API on 3000."

SKIPPING ITEMS:
  canary sandbox checklist skip <template-id> <item-id>

  Use when an item doesn't apply (e.g., no seed data needed, no integration tests).

SPECIAL: docker-compose-created accepts --config-schema to define user-configurable env vars:
  canary sandbox checklist complete <template-id> docker-compose-created \\
    --status success \\
    --summary "Compose with postgres, redis, api, web" \\
    --config-schema '[{"key": "ANTHROPIC_API_KEY", "required": true, "sensitive": true, "description": "API key"}]'

TYPICAL CHECKLIST ORDER:
  1. Provisioned          — done automatically when you create the template
  2. Code Cloned          — done automatically
  3. Environment Analyzed — complete after your research phase
  4. Setup Script Created — complete after uploading setup-script artifact
  5. Dockerfile Created   — complete after uploading dockerfile artifact
  6. Docker Compose Created — complete after uploading docker-compose artifact (with config-schema)
  7. Unit Tests Runnable  — complete or skip. Install runtime on host via setup script,
                            or use --service to run inside a container
  8. Integration Tests Runnable — complete or skip. Use --service to run inside a
                            container with access to database and other services
  9. Seed Script Created  — complete or skip
  10. Server Running       — complete after API responds to health checks
  11. Server Accessible   — complete after nginx routes to the app successfully
  12. Template Verified   — provision a fresh instance and verify it comes up healthy

TEMPLATE VERIFICATION (final step):
  Run one command — it provisions a fresh instance, waits for the app to
  come up, marks template-verified, and terminates the instance:
    canary sandbox verify <template-id>

  Pass --keep-instance to leave the instance running afterwards (useful if
  you want to poke at it).

  This catches issues that authoring hides:
    - Config values set manually but not in configDefaults
    - SANDBOX_URL or other platform vars not properly referenced
    - Startup scripts that assume manual setup already happened

  Template status flow:
    provisioning → authoring → verified

  'verify' is the one transition from authoring to verified, and it is one-way
  — there is no re-authoring path back to 'authoring' from 'verified'. To make
  an AI-assisted edit to a verified template's artifacts, provision a sandbox
  with the agent attached:

    canary sandbox provision <template-id> --with-agent

  then iterate via in-sandbox agent chat or 'canary sandbox artifact create',
  and re-run 'canary sandbox verify' against the same template id when you're
  happy with the changes. To start over from scratch, use 'regenerate' to
  delete existing artifacts, or create a new template.
`.trim(),
};

const ACTIONS: GuideSection = {
  title: 'Actions',
  slug: 'actions',
  content: `
Actions are template-defined, parameterized operations that run against sandbox
instances. Unlike run-command (arbitrary shell), actions have typed input/output
schemas, are reusable across instances, and produce an auditable execution log.

IN-BOX AGENT: the sandbox agent defines actions with the publish_action tool,
lists them with list_actions, and RUNS them with execute_action (by slug, with
params) — all scoped to its own instance, no instance id needed. The CLI
equivalents below are for external operators.

USE CASES:
  - Provision a user credential (username, password, role)
  - Seed demo data for a specific scenario
  - Run database migrations
  - Reset application state
  - Toggle feature flags

MANAGING ACTIONS:

  List actions on a template:
    canary sandbox action list <template-id>

  View an action definition (including script):
    canary sandbox action get <template-id> <slug>

  Create or update an action:
    canary sandbox action create <template-id> <slug> \\
      --label "Provision Credential" \\
      --description "Create a user account in the sandbox app" \\
      --icon UserPlus \\
      --script ./scripts/provision-user.sh \\
      --input-schema ./schemas/provision-user-input.json \\
      --credential-output-config ./schemas/provision-user-credential.json

  --credential-output-config is optional. When set, a successful execution
  also creates/updates a credential in the property's vault — see "Linking
  actions to Canary credentials" below.

  Delete an action:
    canary sandbox action delete <template-id> <slug>

EXECUTING ACTIONS:

  Execute against a running instance:
    canary sandbox action execute <instance-id> <slug> \\
      --param username=testuser \\
      --param password=s3cret \\
      --param role=admin

  Or pass all params from a JSON file:
    canary sandbox action execute <instance-id> <slug> \\
      --params-file ./params.json

  View execution history:
    canary sandbox action executions <instance-id>

SLUG NAMING:
  Slugs must be lowercase alphanumeric with hyphens, starting with a letter:
    provision-credential    ✓
    seed-demo-data          ✓
    reset-state             ✓
    123-bad                 ✗
    UPPER_CASE              ✗

INPUT SCHEMA:
  A JSON Schema file defining the action's parameters. The schema drives
  validation, CLI prompting, and UI form generation.

  Example (provision-user-input.json):
    {
      "type": "object",
      "properties": {
        "username": {
          "type": "string",
          "description": "Username to create",
          "minLength": 1
        },
        "password": {
          "type": "string",
          "description": "Password for the user",
          "sensitive": true
        },
        "role": {
          "type": "string",
          "description": "Role to assign",
          "enum": ["admin", "user", "viewer"],
          "default": "user"
        }
      },
      "required": ["username", "password"]
    }

  The "sensitive" extension marks params that should be masked in UI and
  injected securely (via tempfile, not env var) on the instance.

SCRIPT CONVENTIONS:
  Action scripts are bash, executed on the instance via SSM. Parameters are
  injected as INPUT_<KEY> environment variables, where <KEY> is the param
  name uppercased with non-alphanumeric chars replaced by '_'. There is NO
  camelCase → snake_case conversion. Examples:
    orgName   → INPUT_ORGNAME    (NOT INPUT_ORG_NAME)
    org_name  → INPUT_ORG_NAME
    api-key   → INPUT_API_KEY
  If you want INPUT_FOO_BAR, name the input schema key 'foo_bar' or 'foo-bar'.

  The last line of stdout that is valid JSON is parsed as the structured result.

  EXECUTION ENVIRONMENT — same architecture as startup scripts:
    Scripts run on the HOST (bare EC2 instance). The host has docker, curl,
    and basic linux tools — but NO app runtimes (node, bun, python, ruby).
    Those live inside your Docker containers.

    Use "docker compose exec" to run commands that need the app runtime:
      sb-compose exec -T app-api node -e "..."     # Node.js
      sb-compose exec -T app-api bun run seed.ts   # Bun
      sb-compose exec -T app-db psql -U app -d db  # Database CLI

    Use bare host commands for docker/system operations:
      sb-compose restart app-api                    # Restart a service
      curl -s http://localhost:3000/health              # Health check
      cat /workspace/.canary/.env                       # Read config

    The -T flag is required for docker compose exec in non-interactive (SSM)
    contexts. Without it, exec tries to allocate a TTY and hangs.

    Working directory is /workspace (the mounted repo root). Keep script logic
    repo-root-relative by starting with "cd /workspace" when needed.

  AVOID INLINE CODE IN ACTION SCRIPTS:
    Never embed application-language code (JS, Python, etc.) directly in the
    bash script via "bun -e" or "node -e". Shell escaping of quotes, template
    literals, and variable interpolation is fragile and hard to debug.

    Instead, write complex logic as a separate file artifact and execute it:
      1. Write a script file (e.g. provision-user.ts)
      2. Upload as a file artifact:
           canary sandbox artifact create <template-id> file provision-user.ts \\
             --path /workspace/scripts/provision-user.ts
      3. Reference it from the action script:
           sb-compose exec -T app-api bun /app/scripts/provision-user.ts

    Parameters are available in the action script as INPUT_<KEY> env vars.
    Pass them to the file artifact via env or args:
      sb-compose exec -T -e EMAIL="$INPUT_EMAIL" app-api bun /app/scripts/provision-user.ts

  Example — provision a user (two-file pattern):

    Place the .ts file under the workspace whose node_modules has the deps
    (e.g. apps/api when only apps/api depends on \`postgres\`). Bun resolves
    modules from the script's directory walking up — if the dep isn't hoisted
    to the repo-root node_modules, a script under /workspace/scripts/ will
    fail with "Cannot find package 'postgres'". Use \`-w\` to set cwd inside
    the right workspace.

    Action script (provision-user.sh):
      #!/bin/bash
      set -euo pipefail
      cd /workspace
      sb-compose exec -T \\
        -w /app/apps/api \\
        -e EMAIL="$INPUT_EMAIL" \\
        -e PASSWORD="$INPUT_PASSWORD" \\
        -e NAME="$INPUT_NAME" \\
        app-api bun /app/apps/api/scripts/provision-user.ts

    File artifact (uploaded to /workspace/apps/api/scripts/provision-user.ts):
      import postgres from "postgres";
      const sql = postgres(process.env.DATABASE_URL!);
      const hash = await Bun.password.hash(process.env.PASSWORD!, { algorithm: "argon2id" });
      const email = process.env.EMAIL!.toLowerCase();
      const [org] = await sql\`INSERT INTO organizations (name) VALUES (\${email + "'s Org"}) RETURNING id\`;
      const [user] = await sql\`
        INSERT INTO users (auth_provider, provider_id, primary_email, password_hash, email_verified_at)
        VALUES ('email', \${email}, \${email}, \${hash}, now()) RETURNING id\`;
      await sql\`INSERT INTO org_memberships (user_id, org_id, role) VALUES (\${user.id}, \${org.id}, 'admin')\`;
      const loginUrl = \`\${process.env.SANDBOX_PUBLIC_URL ?? ''}/login\`;
      // Echo password back so credentialOutputConfig (below) can resolve $output.password.
      // Output keys referenced by credentialOutputConfig.fieldMapping are auto-redacted
      // from stored stdout/logs by the platform — safe to include.
      console.log(JSON.stringify({ ok: true, email, password: process.env.PASSWORD, userId: user.id, orgId: org.id, loginUrl }));
      await sql.end();

LINKING ACTIONS TO CANARY CREDENTIALS:

  When you want a successful action to also produce a credential in the
  property's vault (so it can drive browser logins, seed workflows, etc.),
  attach a credentialOutputConfig. The platform resolves \`$output.<key>\`
  tokens against the action's last-line JSON stdout, then upserts a
  credential scoped to (sandboxActionId, environmentId) — re-running the
  action replaces the credential rather than duplicating it.

  Provide it with --credential-output-config on action create/update:

    canary sandbox action create <template-id> provision-credential \\
      --label "Provision Credential" \\
      --script ./scripts/provision-user.sh \\
      --input-schema ./schemas/provision-user-input.json \\
      --credential-output-config ./schemas/provision-user-credential.json

  Example provision-user-credential.json (matches the script above):
    {
      "fieldMapping": {
        "username": "$output.email",
        "password": "$output.password"
      },
      "loginUrl": "$output.loginUrl",
      "loginInstructions": "Sign in via the email/password form."
    }

  Field-mapping values are either literal strings or \`$output.<key>\` tokens
  resolved from the action's parsed JSON output. Output keys referenced by
  fieldMapping/loginUrl are automatically masked in stored stdout and result.

  After execution, \`canary sandbox action execute\` prints the linked
  credentialId, and \`canary credential list --property <name>\` shows the
  vault entry. \`canary credential test <credId>\` runs the browser login
  against the sandbox URL.

  Example — simple SQL-only action (inline is fine for short commands):
    #!/bin/bash
    set -euo pipefail
    cd /workspace
    sb-compose exec -T app-db psql -U app -d appdb -t --csv -c \\
      "INSERT INTO users (username, password_hash, role)
       VALUES ('$INPUT_USERNAME', crypt('$INPUT_PASSWORD', gen_salt('bf')), '$INPUT_ROLE')
       RETURNING id" | while read -r user_id; do
      echo "{\\"userId\\":\\"$user_id\\",\\"username\\":\\"$INPUT_USERNAME\\",\\"loginUrl\\":\\"https://$SANDBOX_PUBLIC_URL/login\\"}"
    done

  Example — seed data via app CLI:
    #!/bin/bash
    set -euo pipefail
    cd /workspace
    sb-compose exec -T app-api bun run scripts/seed.ts \\
      --scenario "$INPUT_SCENARIO" \\
      --count "$INPUT_COUNT"

  Example — run migrations (no params needed):
    #!/bin/bash
    set -euo pipefail
    cd /workspace
    sb-compose exec -T app-api bash -c \\
      'cd /app/packages/db && bun run drizzle-kit migrate'
    echo '{"status":"completed"}'

  Available context variables:
    SANDBOX_PUBLIC_URL    — the instance's public URL
    SANDBOX_INSTANCE_ID  — the instance UUID

PERSONAS (credential templates) & ACTION FLAGS:
  A "persona" (the customer-facing term for a credential template — "canary
  persona", alias "credential-template") is the object a credential belongs to.
  Credentials LINK to a persona and fill its declared variables ($var.USER.<NAME>).
  Wire a login action to a persona so every instance auto-provisions its credential
  INTO the persona:
    --credential-template <personaId>   Link this action's credential to a persona;
                                        login nodes using that persona auto-provision.
    --provisioning-mode shared|unique   shared = provision once & reuse (default);
                                        unique = one credential per run (script must
                                        take a runId input param).
    --run-on-provision                  Run automatically once per provisioned
                                        instance (a base seed, NOT login-triggered).
                                        Its --output-schema keys are exposed to flows
                                        as $var.SEED.<key>.
  Manage personas directly:
    canary persona create --application <app> --name <name> [--variable NAME[:sensitive]]
    canary persona link   --application <app> <personaId> --credential <credId>

  Heads-up on "canary credential create": it binds to the application's DEFAULT
  environment (usually Production) and has no CLI delete. For a SANDBOX-scoped
  credential, use an action's credentialOutputConfig (scoped to the environment the
  action runs in) — see the SEED & PERSONAS section.

AGENT AUTHORING:
  During template creation, the sandbox agent can publish actions alongside
  artifacts using the publish_action tool. The agent analyzes the codebase
  and generates action definitions for common operations (user creation,
  data seeding, migrations, etc.). These are stored on the template and
  available on all instances provisioned from it.

  publish_action accepts an optional credentialOutputConfig — for any action
  that provisions login material (e.g. a user with email/password), the agent
  should set this so the resulting credential lands in the property vault and
  can be exercised by browser automation.
`.trim(),
};

const EXTERNAL_TEST_RUNNER: GuideSection = {
  title: 'External Test Runner Actions',
  slug: 'external-test-runner',
  content: `
An external test suite (a customer's own Selenium/Playwright/etc. repo) is run
by executing a normal sandbox ACTION on a leased instance — see the ACTIONS
section above for the general action contract. There is no platform-published
runner image and no container the platform launches on your behalf: the action
runs the CUSTOMER'S OWN suite, however the customer's own repo already runs it
(their own \`docker compose\`, their own toolchain, already checked out onto the
instance by however the template or the action itself gets the code there).
The platform's only job is to run your script and then upload whatever it
finds at paths you declared. This section documents the ADDITIONAL contract
that applies only when an action is configured as a suite's runner: a small
set of reserved params the platform injects on every launch, and the OUTPUT
CONFIG that tells the platform what to upload when your script finishes. Get
either wrong and the attempt fails with a thin diagnostic trail, which is why
this gets its own section rather than a footnote on ACTIONS.

RESERVED LAUNCH PARAMS:
  The platform supplies these four params on every launch of a runner action,
  as INPUT_<KEY> env vars, IN ADDITION TO whatever params the suite's own
  config supplies:
    runner_command    → INPUT_RUNNER_COMMAND    the shell command that runs the
                                                 suite (e.g. what the suite's
                                                 config says to run)
    runner_launch_key → INPUT_RUNNER_LAUNCH_KEY unique per attempt; name any
                                                 container you start after this
    runner_env_json   → INPUT_RUNNER_ENV_JSON   JSON object of env vars the
                                                 suite's config wants visible
                                                 to the test process (target
                                                 URL, capture flags, …)
    runner_timeout_ms → INPUT_RUNNER_TIMEOUT_MS wall-clock budget in ms

  Do NOT declare any of these four in the action's --input-schema. They are on
  a platform allowlist and are injected regardless of what the schema says —
  an action used as a suite runner may declare its OWN additional params (the
  suite fills those in from its own config), but redeclaring a reserved name
  is at best redundant and at worst risks fighting the platform-supplied
  value.

  A runner action also must not:
    - declare any "required" param the suite does not itself set (the launch
      will fail validation before the script ever starts), or
    - declare a "sensitive" param — the suite's stored action params are kept
      as plaintext, and a sensitive param is rejected at suite-save time.

OUTPUT CONFIG — how results get back to the platform WITHOUT a curl:
  Your script does not upload anything itself. Instead the action DECLARES,
  in its outputConfig, one or more slots naming a file path your script is
  expected to leave something at when it finishes. The platform appends a
  generated EPILOGUE to your script — code you never write — that gzips
  whatever exists at each declared path and uploads it for you, minting its
  own presigned URL at the moment it uploads (so a multi-hour suite's upload
  is never signed against credentials that expired an hour into the run).

  Two output kinds matter for a runner:
    test-results       kind: "test-results", format: "mochawesome-json"
                        source: { from: "file", path: "/workspace/.canary/<name>" }
                        The parsed report the platform folds into the run's
                        catalog of pass/fail verdicts. Required for a runner
                        action to mean anything.
    artifacts-archive   kind: "artifacts-archive", format: "tar-gz"
                        source: { from: "file", path: "/workspace/.canary/<name>" }
                        A gzipped tar of whatever else is worth keeping —
                        capture video, traces, per-test logs. Optional; a
                        suite with no capture producer has nothing to put here.

  Every declared path MUST be under /workspace/.canary/ — that constraint is
  enforced server-side (validateOutputSlot), not a style preference, and a
  path outside it is refused at save time, not at launch.

  Set outputConfig the same way --credential-output-config sets a credential
  slot: a JSON body on the action's create/update call, e.g.
    "outputConfig": [
      {
        "slot": "test-results",
        "kind": "test-results",
        "config": { "format": "mochawesome-json" },
        "source": { "from": "file", "path": "/workspace/.canary/output/mochawesome.json" }
      },
      {
        "slot": "artifacts-archive",
        "kind": "artifacts-archive",
        "config": { "format": "tar-gz" },
        "source": { "from": "file", "path": "/workspace/.canary/artifacts.tar.gz" }
      }
    ]

  Note the archive path is OUTSIDE /workspace/.canary/output/. You build it by
  taring that directory, so writing it inside the directory tars the archive
  into itself — a recursion that looks fine on a small run and blows up on a
  real one.

LIVE PROGRESS — two host paths the platform reads WHILE your suite runs:

  /workspace/.canary/output/capture/boundaries.jsonl
    One JSON line per finished test. The platform tails this every ~30s and
    appends results to the run as they happen, so a suite that is killed before
    it finishes still reports everything it got through. Without it, a run that
    never reaches its epilogue reports NOTHING — the report is written once, at
    the very end, so there is nothing left behind to recover from.

    On mocha you get the records for free: the platform stages a hook at
    /workspace/.canary/mocha-boundary.cjs before your action starts. Your script
    has to do two things or it stays completely inert —
      - "require" that file from the mocha config you pass, and
      - export COFACTOR_CAPTURE_DIR to the directory it should write into.

    If your suite runs inside a container, that directory is inside the
    container, so publish it to the host path above on a timer (a background
    loop copying it out every ~15s). The platform deliberately never reaches
    into your containers — it only reads the host path. Copy to a temp file and
    mv it into place, so a reader cannot catch a half-written file.

  /workspace/.canary/output/run.log
    Tee your suite's output here. SSM returns stdout only once the invocation
    COMPLETES and caps it around 24KB, and "docker logs" shows nothing when the
    suite runs under "docker exec" — so without this a run is a black box for
    its whole duration. Take the exit code from PIPESTATUS[0], not $?: with
    pipefail set, a failing tee would otherwise be reported as a failing suite.

THE FAILURE MODE — read this before you write the script:
  There is no bootstrap handshake to get wrong anymore, but there is a
  quieter failure this substrate makes MORE likely, not less: your script can
  exit 0 having never actually run the suite (a compose file that doesn't
  exist at the path you assumed, a toolchain not installed on this template,
  a typo in the run command), and the platform has no way to distinguish that
  from a suite that legitimately produced zero tests. If the declared
  test-results path has nothing at it when your script exits, the attempt is
  recorded as "unable to verify" and salvage runs — it does NOT read as a
  passing run, but it also does not tell you WHY nothing landed.

  When that happens, the action's own stdout/stderr (its SSM execution
  transcript) is the ONLY evidence that exists anywhere; the platform
  automatically splices it into the attempt precisely because this failure
  mode produces nothing else to look at. So: prefer a script that fails
  LOUDLY (non-zero exit, a clear stderr line) over one that silently does
  nothing and reports success.

  Name any container you start after $INPUT_RUNNER_LAUNCH_KEY. That gives
  your script the same idempotency the platform's own adoption check gets
  elsewhere, so a re-delivered launch can't start a second copy of the suite
  even if that check were somehow bypassed.

REFERENCE SCRIPT:

  #!/bin/bash
  set -euo pipefail

  # Fail loudly rather than silently: these four are supplied by the platform
  # on every runner launch and are never declared in this action's input schema.
  : "\${INPUT_RUNNER_COMMAND:?missing}"
  : "\${INPUT_RUNNER_LAUNCH_KEY:?missing}"
  : "\${INPUT_RUNNER_ENV_JSON:?missing}"
  : "\${INPUT_RUNNER_TIMEOUT_MS:?missing}"

  OUT_DIR=/workspace/.canary/output
  mkdir -p "$OUT_DIR"

  # The entrypoint enforces its own budget first; this is the shell-level
  # backstop so a wedged suite can't outlive the attempt's timeout.
  timeout_s=$(( (INPUT_RUNNER_TIMEOUT_MS + 999) / 1000 ))

  echo "runner: running the suite's own compose file (timeout \${timeout_s}s)" >&2

  # The suite is the customer's own — this repo's own docker-compose.yml, its
  # own toolchain, already checked out onto this template. This action does
  # not pull or run a platform-published image; it runs what is already here.
  # THE MOUNT IS LOAD-BEARING. Without it this script silently hands back
  # nothing: $INPUT_RUNNER_COMMAND writes its report to $OUT inside the
  # container (/work/output), the container is --rm, and the host never sees
  # the file — so the path outputConfig declares stays empty. Bind the host
  # handover directory onto that path and the suite writes straight through.
  #
  # If your suite instead runs in an ALREADY-RUNNING compose service (via
  # "sb-compose exec"), you cannot add a mount after the fact. Copy it out
  # afterwards, and do so regardless of exit code — a red suite is when the
  # report matters most:
  #     sb-compose cp <service>:<in-container-report> "$OUT_DIR/mochawesome.json"
  timeout "\${timeout_s}s" docker compose run --rm \\
    -v "$OUT_DIR:/work/output" test-runner \\
    sh -c "$INPUT_RUNNER_COMMAND" \\
    || echo "runner: suite exited non-zero (this may just mean tests failed)" >&2

  # No upload code below this line. The platform's generated epilogue reads
  # $OUT_DIR/mochawesome.json and $OUT_DIR/artifacts.tar.gz — whichever paths
  # this action's outputConfig declared — gzips them, and uploads them after
  # your script returns. If mochawesome.json is missing here, that is exactly
  # what the platform will see too: report the paths your outputConfig names
  # so a debugging session does not have to guess.
  echo "runner: script complete, handing off to the platform's upload epilogue" >&2

  Note what is absent: no $INPUT_RUNNER_CALLBACK_URL, no bootstrap token, no
  curl to an upload-url endpoint, no "\${status: launched}" line the platform
  parses. The epilogue is appended automatically and runs after this script
  returns — writing the two files above is the entire contract.

DIAGNOSING A DEAD RUNNER ATTEMPT:
  If an attempt on this substrate stops abnormally, the platform logs a remedy
  alongside the stop code:
    failed-to-start   → the action produced no exit code; check the script
                        itself for a syntax error or an unset reserved var
    oom               → the suite exhausted the instance's memory; raise the
                        template's instanceSize
    host-terminated   → the sandbox host went away mid-run; check the TTL
                        reaper and any scale-in activity against the lease
    unknown           → the attempt ended without an observed outcome; check
                        whether the host was terminated before the epilogue
                        could upload anything
  Pair that remedy with the spliced action transcript. If the transcript looks
  clean but the attempt still shows no report, the near-certain cause is a
  declared outputConfig path the script never actually wrote to — check the
  path in outputConfig against the path the suite's own reporter is
  configured to write.
`.trim(),
};

const SEED_PERSONAS: GuideSection = {
  title: 'Seed & Personas',
  slug: 'seed-personas',
  content: `
Populate a demo dataset on boot and wire login personas so a sandbox is worth
demoing. Three moving parts: the seed pipeline, persona homing, and persona-linked
credentials.

SEED PIPELINE (base → enrichment)
  Real seed systems ENRICH an already-populated workspace rather than invent core
  entities: a BASE seed creates the org / workspace / users / tickets, then
  ENRICHMENT layers feature data on top (enrichment fails if the base workspace is
  missing). Chain them in one script. Two guards you will hit:
    - Local-only guards: seeders refuse a non-local DB. A compose service host is
      NOT "localhost" — set the app's escape hatch (e.g. SEED_ALLOW_NONLOCAL_HOST=true)
      and keep ENVIRONMENT=development.
    - Seeded-rows boot guard: apps that block booting with seeded rows need a flag
      (e.g. VEDAYA_ALLOW_SEEDED_ROWS=true) in the api compose env, or a later reboot
      of the api refuses to start.

RUN IT ON BOOT — detached
  Put the pipeline in the SEED SCRIPT (runs once per fresh provision, guarded by
  .canary/.seeded). A full demo seed takes minutes — far longer than the
  provision/verify health gate — so run it DETACHED and let it finish in the
  background; provision/verify only gate on app health, not on data being present:
    PG_IP=$(sb-compose exec -T app-api getent ahostsv4 postgres | head -1 | awk '{print $1}')
    sb-compose exec -d -e DATABASE_URL=postgresql://u:p@$PG_IP:5432/db app-api \\
      bash -c '(./scripts/demo-seed.sh; \\
                psql "$DATABASE_URL" -f /app/.canary/home-personas.sql; \\
                echo SEED_DONE) > /app/.canary/seed.log 2>&1'
  Tail /workspace/.canary/seed.log for your DONE marker. (Alternative: an ACTION
  with --run-on-provision runs once per instance and exposes $var.SEED.* to flows.)

PERSONA HOMING — the workspace-mismatch trap
  Apps often ship dev-login "personas" (quick-login buttons) whose accounts get
  homed in a DEFAULT workspace — NOT the workspace your demo seed populated. So the
  persona buttons log into an EMPTY workspace. Fix: after seeding, re-home the
  persona accounts (users.workspace_id + org membership + role assignment) into the
  seeded workspace with a small idempotent SQL file (home-personas.sql above).
  dev-login typically PRESERVES an existing home workspace, so the re-home sticks
  across logins. Verify: the persona's workspace should equal the workspace holding
  the demo rows (e.g. tickets).

PERSONA-LINKED CREDENTIALS (sandbox-scoped)
  Make each persona a Cofactor credential bound to THIS sandbox so flows/tests can
  log in as it:
    1. Create the persona (credential template):
         canary persona create --application <app> --name "IT Admin"
    2. Add a login ACTION per persona whose script dev-logs-in the persona and
       echoes {"email": ..., "loginUrl": "\${SANDBOX_PUBLIC_URL}/login"} on its
       LAST stdout line.
    3. Attach BOTH:
         --credential-output-config  (fieldMapping username=$output.email,
             loginUrl=$output.loginUrl, loginInstructions describing the login —
             often passwordless / persona-button, so instruction-driven)
         --credential-template <personaId>  (auto-links the provisioned credential
             INTO the persona on every instance)
    4. Execute the action against the instance → a credential scoped to that
       instance's environment and linked to the persona. Re-run on a new instance
       to rebind. Verify with:  canary credential test <credId>
  Why not "canary credential create": it binds to the app's DEFAULT (Production)
  environment and cannot be deleted from the CLI. The action route above is the
  sandbox-scoped, re-runnable path.
`.trim(),
};

const CACHING: GuideSection = {
  title: 'Build Caching (advanced)',
  slug: 'caching',
  content: buildCaching.trim(),
};

export const SANDBOX_GUIDE_SECTIONS: GuideSection[] = [
  GOAL,
  RESEARCH,
  ARTIFACTS,
  ACTIONS,
  EXTERNAL_TEST_RUNNER,
  SEED_PERSONAS,
  VERIFICATION,
  CHECKLIST,
  CACHING,
];
